import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
    MessageCircle, Smartphone, Shield, BarChart2, Globe, Zap, ArrowRight,
    CheckCircle, Users, Clock,
} from 'lucide-react';
import { FloatingPaths } from '@LandingFunctionalDisplay/BackgroundPaths';
import TopPanel from '@CommonFunctionalDisplay/TopPanel';
import Footer from '@CommonFunctionalDisplay/Footer';
import useSEO from '@Hooks/useSEO';
import './WhyKaizIQPage.scss';

const DIFFERENTIATORS = [
    {
        icon: <MessageCircle size={28} />,
        tag: 'Communication',
        title: 'WhatsApp-Native From the Ground Up',
        body: 'Most support tools treat WhatsApp as an afterthought. KaizIQ Resolve was designed with WhatsApp at the core — customers receive every ticket update, assignment, and resolution directly on WhatsApp, the channel they actually check. No apps to download, no email overload.',
        stat: { value: '98%', label: 'WhatsApp open rate vs 20% for email' },
        flip: false,
    },
    {
        icon: <Globe size={28} />,
        tag: 'No-Code',
        title: 'Build Any Support Form Without Engineering',
        body: 'Our drag-and-drop form builder lets ops managers, HR leads, and IT coordinators create structured intake forms in minutes — no developer needed. Custom fields, conditional logic, file attachments, and branded URLs out of the box.',
        stat: { value: '<1 hr', label: 'From idea to live form' },
        flip: true,
    },
    {
        icon: <Smartphone size={28} />,
        tag: 'Mobile First',
        title: 'A White-Label Mobile App That Is Already Built',
        body: "Most platforms charge enterprise fees for a custom mobile app — or make you build it yourself. KaizIQ Desk is our pre-built, white-label iOS & Android app that you can deploy under your own brand. Your logo, your colours, your domain — ready today.",
        stat: { value: '0', label: 'Engineering hours to deploy' },
        flip: false,
    },
    {
        icon: <Shield size={28} />,
        tag: 'Governance',
        title: 'SLA Enforcement That Keeps Teams Accountable',
        body: 'Set response and resolution SLAs per ticket priority. Automated escalation ensures urgent issues reach senior staff before deadlines breach. Every SLA breach is logged, every escalation is tracked — complete accountability across your organisation.',
        stat: { value: '40%', label: 'Reduction in SLA breaches' },
        flip: true,
    },
    {
        icon: <BarChart2 size={28} />,
        tag: 'Insights',
        title: 'Analytics That Actually Help You Improve',
        body: "Beyond basic counts, KaizIQ Resolve surfaces trends that matter: agent workload imbalances, repeat issue patterns, high-complaint products, and SLA performance by team. Data you can act on, not just dashboards to screenshot for slide decks.",
        stat: { value: '360°', label: 'Operational visibility' },
        flip: false,
    },
];

const PILLARS = [
    { icon: <Zap size={20} />, title: 'Fast to Deploy', desc: 'Go from sign-up to first ticket in under 60 minutes. No implementation consultant needed.' },
    { icon: <Users size={20} />, title: 'Built for Real Teams', desc: 'Designed for IT teams, HR teams, and ops teams — not just call centre agents.' },
    { icon: <Clock size={20} />, title: 'Transparent Pricing', desc: 'Flat per-agent pricing with no hidden setup fees, no training costs, and a free tier that actually works.' },
    { icon: <CheckCircle size={20} />, title: 'No Lock-In', desc: 'Export all your data anytime. Month-to-month contracts. Cancel whenever you want.' },
];

function Reveal({ children, className = '', delay = 0 }) {
    return (
        <motion.div
            className={className}
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay }}
        >
            {children}
        </motion.div>
    );
}

export default function WhyKaizIQPage() {
    const navigate = useNavigate();
    const cn = 'whyKaizIQ';

    const seo = useSEO({
        title:       'Why KaizIQ Resolve — Built for How Teams Actually Work',
        description: 'Discover why KaizIQ Resolve is different: WhatsApp-native notifications, no-code setup, white-label mobile app, and transparent pricing. No legacy helpdesk bloat.',
        canonical:   'https://resolve.kaiziq.io/#/why-kaiziq',
        schema: {
            '@context': 'https://schema.org',
            '@type':    'WebPage',
            name:       'Why KaizIQ Resolve',
            url:        'https://resolve.kaiziq.io/#/why-kaiziq',
            description: 'Why KaizIQ Resolve is the right ticket management platform for modern teams.',
        },
    });

    return (
        <div className={cn}>
            {seo}
            <TopPanel />

            {/* Hero */}
            <section className={cn + '__hero'}>
                <FloatingPaths position={1} />
                <FloatingPaths position={-1} />
                <div className={cn + '__hero__inner'}>
                    <motion.div className={cn + '__hero__tag'} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45 }}>
                        Why KaizIQ
                    </motion.div>
                    <motion.h1 className={cn + '__hero__title'} initial={{ opacity: 0, y: 28 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.55, delay: 0.1 }}>
                        A support platform built for<br />how teams actually work
                    </motion.h1>
                    <motion.p className={cn + '__hero__sub'} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.18 }}>
                        KaizIQ Resolve is not a repurposed CRM or a legacy helpdesk with a new coat of paint. It was designed from scratch for the WhatsApp era, with no-code setup and transparent pricing that scales with your team.
                    </motion.p>
                    <motion.div className={cn + '__hero__actions'} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.45, delay: 0.28 }}>
                        <button className={cn + '__hero__cta--primary'} onClick={() => navigate('/login')}>
                            Get Started Free <ArrowRight size={16} />
                        </button>
                        <button className={cn + '__hero__cta--ghost'} onClick={() => navigate('/pricing')}>
                            See Pricing
                        </button>
                    </motion.div>
                </div>
            </section>

            {/* Pillars */}
            <section className={cn + '__pillars'}>
                {PILLARS.map((p, i) => (
                    <Reveal key={i} className={cn + '__pillar'} delay={i * 0.08}>
                        <div className={cn + '__pillar__icon'}>{p.icon}</div>
                        <div>
                            <h4 className={cn + '__pillar__title'}>{p.title}</h4>
                            <p className={cn + '__pillar__desc'}>{p.desc}</p>
                        </div>
                    </Reveal>
                ))}
            </section>

            {/* Differentiators */}
            <section className={cn + '__diff'}>
                {DIFFERENTIATORS.map((d, i) => (
                    <Reveal key={i} className={cn + '__diff__row' + (d.flip ? ' ' + cn + '__diff__row--flip' : '')}>
                        <div className={cn + '__diff__content'}>
                            <div className={cn + '__diff__tag'}>{d.tag}</div>
                            <div className={cn + '__diff__icon'}>{d.icon}</div>
                            <h2 className={cn + '__diff__title'}>{d.title}</h2>
                            <p className={cn + '__diff__body'}>{d.body}</p>
                            <div className={cn + '__diff__stat'}>
                                <span className={cn + '__diff__stat__value'}>{d.stat.value}</span>
                                <span className={cn + '__diff__stat__label'}>{d.stat.label}</span>
                            </div>
                        </div>
                        <div className={cn + '__diff__visual'}>
                            <div className={cn + '__diff__card'}>
                                <div className={cn + '__diff__card__icon'}>{d.icon}</div>
                                <div className={cn + '__diff__card__stat'}>{d.stat.value}</div>
                                <div className={cn + '__diff__card__label'}>{d.stat.label}</div>
                            </div>
                        </div>
                    </Reveal>
                ))}
            </section>

            {/* CTA */}
            <section className={cn + '__cta'}>
                <Reveal className={cn + '__cta__inner'}>
                    <h2 className={cn + '__cta__title'}>Ready to see the difference?</h2>
                    <p className={cn + '__cta__sub'}>Start free. No credit card. No lock-in. Just better support.</p>
                    <button className={cn + '__cta__btn'} onClick={() => navigate('/login')}>
                        Start Free Trial <ArrowRight size={16} />
                    </button>
                </Reveal>
            </section>

            <Footer />
        </div>
    );
}
